/** Fixed width for the sticky metric-label column across all metrics tables. */
export const METRICS_TABLE_LABEL_COL_WIDTH = 180;

/** Minimum width per data column before the table scrolls horizontally. */
export const METRICS_TABLE_MIN_DATA_COL_WIDTH = 100;

/** Wider minimum for livestream emission columns (title + date headers). */
export const METRICS_TABLE_STREAM_COL_MIN_WIDTH = 148;

export const metricsTableLabelColSx = {
  width: METRICS_TABLE_LABEL_COL_WIDTH,
  minWidth: METRICS_TABLE_LABEL_COL_WIDTH,
  maxWidth: METRICS_TABLE_LABEL_COL_WIDTH,
  boxSizing: "border-box" as const,
};

export const metricsTableStickyLabelColSx = {
  ...metricsTableLabelColSx,
  position: "sticky" as const,
  left: 0,
  zIndex: 1,
  bgcolor: "background.paper",
};

export const metricsTableStickyHeaderLabelColSx = {
  ...metricsTableStickyLabelColSx,
  zIndex: 3,
};

/** Row label cell (first column body rows). */
export const metricsTableLabelCellSx = {
  fontWeight: 500,
  color: "text.secondary",
  whiteSpace: "nowrap" as const,
  fontSize: "body2.fontSize",
  lineHeight: "body2.lineHeight",
};

export const metricsTableLabelRowCellSx = {
  ...metricsTableStickyLabelColSx,
  ...metricsTableLabelCellSx,
};

/** Data cells (all columns after the label col). */
export const metricsTableDataBodyCellSx = {
  fontWeight: 600,
  color: "text.primary",
};

export const metricsTableDataHeaderCellSx = {
  fontWeight: 700,
  color: "text.primary",
};

/** Equal share of table width for each data column (label col is fixed px). */
export function getMetricsDataColWidth(dataColumnCount: number): string {
  if (dataColumnCount <= 0) return "0px";
  return `calc((100% - ${METRICS_TABLE_LABEL_COL_WIDTH}px) / ${dataColumnCount})`;
}

export function getMetricsTableMinWidth(
  dataColumnCount: number,
  minDataColWidth: number = METRICS_TABLE_MIN_DATA_COL_WIDTH,
): number {
  return (
    METRICS_TABLE_LABEL_COL_WIDTH +
    dataColumnCount * minDataColWidth
  );
}

/** Min width when the first N data columns are fixed (sticky summary cols). */
export function getMetricsTableMinWidthWithLeadingSticky(
  dataColumnCount: number,
  leadingStickyCount: number,
  leadingColWidth: number,
  scrollColWidth: number,
): number {
  const leading = Math.min(leadingStickyCount, dataColumnCount);
  const scrollCount = Math.max(0, dataColumnCount - leading);
  return (
    METRICS_TABLE_LABEL_COL_WIDTH +
    leading * leadingColWidth +
    scrollCount * scrollColWidth
  );
}

const STICKY_COL_SHADOW = "2px 0 4px -2px rgba(0,0,0,0.08)";

/** Sticky left offset for data column index (0 = first data col after labels). */
export function metricsTableStickyDataLeft(colIndex: number, colWidth: number): number {
  return METRICS_TABLE_LABEL_COL_WIDTH + colIndex * colWidth;
}

/** Header/body cell sx for sticky leading data columns (desktop only when `mobileScrollAll`). */
export function metricsTableStickyDataCellSx(
  colIndex: number,
  colWidth: number,
  leadingStickyCount: number,
  mobileScrollAll: boolean,
  variant: "header" | "body",
) {
  if (colIndex >= leadingStickyCount) return {};
  const left = metricsTableStickyDataLeft(colIndex, colWidth);
  const isLastSticky = colIndex === leadingStickyCount - 1;
  const z = variant === "header" ? 2 : 1;
  return mobileScrollAll
    ? {
        position: { xs: "static", md: "sticky" as const },
        left: { md: left },
        zIndex: { md: z },
        bgcolor: { md: "background.paper" },
        ...(isLastSticky
          ? { boxShadow: { md: STICKY_COL_SHADOW } }
          : {}),
      }
    : {
        position: "sticky" as const,
        left,
        zIndex: z,
        bgcolor: "background.paper",
        ...(isLastSticky ? { boxShadow: STICKY_COL_SHADOW } : {}),
      };
}

/** Label column sticky sx; on mobile optionally scrolls with the table. */
export function metricsTableStickyLabelCellSx(mobileScrollAll: boolean) {
  return mobileScrollAll
    ? {
        ...metricsTableLabelColSx,
        position: { xs: "static", md: "sticky" as const },
        left: { md: 0 },
        zIndex: { md: 1 },
        bgcolor: { md: "background.paper" },
      }
    : metricsTableStickyLabelColSx;
}

export function metricsTableStickyHeaderLabelCellSx(mobileScrollAll: boolean) {
  return mobileScrollAll
    ? {
        ...metricsTableLabelColSx,
        position: { xs: "static", md: "sticky" as const },
        left: { md: 0 },
        zIndex: { md: 3 },
        bgcolor: { md: "background.paper" },
      }
    : metricsTableStickyHeaderLabelColSx;
}

export function metricsTableStickyLabelRowCellSx(mobileScrollAll: boolean) {
  return mobileScrollAll
    ? {
        ...metricsTableStickyLabelCellSx(true),
        ...metricsTableLabelCellSx,
      }
    : metricsTableLabelRowCellSx;
}

export const metricsTableBaseSx = {
  width: "100%",
  tableLayout: "fixed" as const,
  borderCollapse: "separate" as const,
  "& th, & td": { borderColor: "divider" },
  // Label col is <th scope="row">; do not use td:first-of-type (that hits col 2).
  "& tbody th[scope='row']": metricsTableLabelRowCellSx,
  "& tbody td": metricsTableDataBodyCellSx,
  "& thead th:not(:first-of-type)": metricsTableDataHeaderCellSx,
};
